export const OVERDUE_TASK_INBOX_PATH = "/equipe/tarefas/inbox?overdue=1";

const GENERIC_OVERDUE_TITLES = new Set([
  "tarefa com prazo vencido",
  "overdue task",
  "tarea con plazo vencido",
  "attività scaduta",
]);

function parseDueMs(value) {
  if (value instanceof Date) {
    const ms = value.getTime();
    return Number.isNaN(ms) ? null : ms;
  }
  const raw = String(value || "").trim();
  if (!raw) return null;
  const ms = Date.parse(raw.includes("T") ? raw : raw.replace(" ", "T"));
  return Number.isNaN(ms) ? null : ms;
}

export function formatOverdueLag(dueInput, nowMs = Date.now(), locale = "pt-BR") {
  const dueMs = parseDueMs(dueInput);
  if (dueMs == null || !(nowMs > dueMs)) return "";
  const diff = nowMs - dueMs;
  const minute = 60_000;
  const hour = 60 * minute;
  const day = 24 * hour;
  const rtf = new Intl.RelativeTimeFormat(locale || "pt-BR", { numeric: "auto" });
  if (diff < hour) return rtf.format(-Math.max(1, Math.round(diff / minute)), "minute");
  if (diff < day) return rtf.format(-Math.max(1, Math.round(diff / hour)), "hour");
  return rtf.format(-Math.max(1, Math.round(diff / day)), "day");
}

function shorten(title, max = 42) {
  const text = String(title || "Tarefa").trim().replace(/\s+/g, " ");
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1)}…`;
}

export function isGenericOverdueTitle(title) {
  return GENERIC_OVERDUE_TITLES.has(String(title || "").trim().toLowerCase());
}

export function isOverdueDigestEntry(entry) {
  const link = String(entry?.deepLink || "");
  if (link.includes("overdue=1")) return true;
  const title = String(entry?.title || "");
  return /\d+\s+/.test(title) && /atrasad|overdue|vencid|scadut/i.test(title);
}

function taskLabelFromEntry(entry) {
  const body = String(entry?.body || "").trim();
  const title = String(entry?.title || "").trim();
  if (body && !isGenericOverdueTitle(body) && body !== title) return body;
  if (title && !isGenericOverdueTitle(title)) return title;
  return "";
}

/**
 * Resumo ao vivo, a partir das tarefas do responsável (mais atrasada primeiro).
 */
export function buildLiveOverdueDigest({ tasks, total, locale = "pt-BR", t, nowMs = Date.now() }) {
  const list = (Array.isArray(tasks) ? tasks : [])
    .filter((item) => item && item.phase === "overdue")
    .slice()
    .sort((a, b) => String(a.at || "").localeCompare(String(b.at || "")));
  const count = Math.max(list.length, Number(total) || 0);
  if (!count || typeof t !== "function") return null;

  const signature = `${count}:${list
    .slice(0, 5)
    .map((item) => item.id)
    .join(",")}`;

  if (count === 1 && list[0]) {
    const item = list[0];
    const when = formatOverdueLag(item.at, nowMs, locale);
    const board = String(item.boardName || "").trim();
    let body = t("taskOverdueDigest.singleBodyNoWhen");
    if (when && board) body = t("taskOverdueDigest.singleBodyBoard", { when, board });
    else if (when) body = t("taskOverdueDigest.singleBody", { when });
    else if (board) body = t("taskOverdueDigest.singleBodyNoWhenBoard", { board });
    return {
      title: item.title || t("titles.taskOverdue"),
      body,
      deepLink: item.deepLink || OVERDUE_TASK_INBOX_PATH,
      signature,
      taskCount: 1,
    };
  }

  const parts = list.slice(0, 3).map((item, index) => {
    const name = shorten(item.title);
    const when = formatOverdueLag(item.at, nowMs, locale);
    return index === 0 && when ? `${name} (${when})` : name;
  });
  const more = Math.max(0, count - parts.length);
  const preview = parts.join(" · ");
  return {
    title: t("taskOverdueDigest.titleMany", { count }),
    body: more > 0 ? t("taskOverdueDigest.bodyMany", { preview, count: more }) : preview,
    deepLink: OVERDUE_TASK_INBOX_PATH,
    signature,
    taskCount: count,
  };
}

/**
 * Colapsa avisos já gravados (inclusive os repetidos) num único texto útil.
 */
export function summarizeStoredOverdueNotifications(items, t) {
  const list = (Array.isArray(items) ? items : []).filter(Boolean);
  if (!list.length || typeof t !== "function") return null;

  if (list.length === 1 && !isOverdueDigestEntry(list[0])) {
    const only = list[0];
    const title = String(only.title || "").trim();
    const body = String(only.body || "").trim();
    if (isGenericOverdueTitle(title) && body && !isGenericOverdueTitle(body)) {
      return {
        title: body,
        body: t("taskOverdueDigest.storedSingle"),
        deepLink: only.deepLink || OVERDUE_TASK_INBOX_PATH,
        taskCount: 1,
      };
    }
    return {
      title: title || t("titles.taskOverdue"),
      body: body && body !== title ? body : t("taskOverdueDigest.storedSingle"),
      deepLink: only.deepLink || OVERDUE_TASK_INBOX_PATH,
      taskCount: 1,
    };
  }

  const digest = list
    .filter(isOverdueDigestEntry)
    .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))[0];
  if (digest) {
    const body = String(digest.body || "").trim();
    const fromTitle = String(digest.title || "").match(/^(\d+)\s+/);
    return {
      title: digest.title || t("taskOverdueDigest.titleMany", { count: list.length }),
      body: body || t("taskOverdueDigest.unknownMany"),
      deepLink: digest.deepLink || OVERDUE_TASK_INBOX_PATH,
      taskCount: fromTitle ? Number(fromTitle[1]) : 1,
    };
  }

  const byAge = list
    .slice()
    .sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0));
  const names = [];
  const seen = new Set();
  for (const entry of byAge) {
    const label = taskLabelFromEntry(entry);
    if (!label) continue;
    const key = label.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    names.push(label);
  }

  if (names.length <= 1) {
    const only = list.length === 1 ? list[0] : byAge.find((entry) => taskLabelFromEntry(entry)) || list[0];
    const title = names[0] || t("titles.taskOverdue");
    return {
      title,
      body: t("taskOverdueDigest.storedSingle"),
      deepLink: names.length === 1 && only?.deepLink ? only.deepLink : OVERDUE_TASK_INBOX_PATH,
      taskCount: 1,
    };
  }

  const previewNames = names.slice(0, 3);
  const more = names.length - previewNames.length;
  const preview = previewNames.join(" · ");
  return {
    title: t("taskOverdueDigest.titleMany", { count: names.length }),
    body: more > 0 ? t("taskOverdueDigest.bodyMany", { preview, count: more }) : preview,
    deepLink: OVERDUE_TASK_INBOX_PATH,
    taskCount: names.length,
  };
}
